import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { DownloadedFile, ListMeta, Query } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * A área de assinaturas (docs/api/assinaturas.md): a lista de trabalho, o painel, o envio, o reenvio,
 * o cancelamento, a assinatura da clínica, a exclusão por root, os downloads, o Desfazer e o aviso no
 * navegador (RN-ASS-32). A página nunca chama `fetch` (T25).
 */

export type SignatureFilter = 'pending' | 'awaiting_patient' | 'signed' | 'all'
export type SignatureSort =
  'severity' | '-sentAt' | 'sentAt' | 'patient' | '-daysOpen' | '-totalAmountCents'
export type Channels = 'whatsapp' | 'email' | 'both'
export type SignatureStatus =
  | 'generated'
  | 'awaiting_patient'
  | 'awaiting_clinic_signature'
  | 'signed'
  | 'not_signed'
  | 'expired'
  | 'canceled'
export type Level = 'critical' | 'attention' | 'waiting' | 'done'
export type RowAction = 'send' | 'resend' | 'copy_link' | 'cancel' | 'sign' | 'download' | 'delete'
/** Quem conduz a assinatura do envio (RN-ASS-33): a Autentique ou as telas da Westhetics. */
export type SignatureProvider = 'autentique' | 'westhetics'
export type DeliveryStatus =
  'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'canceled' | 'not_sent' | 'manual'

export interface DeliveryItem {
  channel: 'whatsapp' | 'email'
  status: DeliveryStatus
  at: string | null
  detail: string | null
  failureCode: string | null
  isPermanent: boolean
}

export interface SignatureDocument {
  id: string
  title: string
  category: string
  templateVersion: string | null
  status: string
  label: string
  level: Level
  hash: string | null
  fullHash: string | null
  pageCount: number | null
  isRestricted: boolean
  readAt: string | null
  patientSignedAt: string | null
  signedAt: string | null
  signedPdf: { hash: string; generatedAt: string } | null
  hasPdf: boolean
}

export interface PendingAction {
  id: string
  kind: 'send' | 'resend' | 'cancel'
  undoableUntil: string
}

export interface DerivedStatus {
  status: SignatureStatus
  label: string
  level: Level
  severity: number
  procedureDoneUnsigned: boolean
  isUrgent: boolean
  daysOpen: number | null
  pendingType: string | null
}

export interface SignatureRow extends DerivedStatus {
  appointmentId: string
  /** O atendimento original quando a linha é o envio de documentos adicionados (RN-DOC-57). */
  parentAppointmentId: string | null
  signatureRequestId: string | null
  protocolNumber: string | null
  patient: { id: string; name: string }
  procedure: string | null
  practitioner: { id: string; name: string } | null
  totalAmountCents: number | null
  type: 'procedure' | 'standalone'
  sentAt: string | null
  generatedAt: string
  procedureDate: string | null
  linkExpiresAt: string | null
  channels: Channels | null
  delivery: DeliveryItem[]
  /** "WhatsApp · lido · E-mail · entregue", pronto para a coluna. */
  deliverySummary: string
  documents: SignatureDocument[]
  patientSignedAt: string | null
  signedAt: string | null
  pendingAction: PendingAction | null
  actions: RowAction[]
}

export interface SignatureListMeta extends ListMeta {
  counts: { pending: number; awaiting_patient: number; signed: number; all: number }
  period: { from: string; to: string }
  providers: Record<string, { state: 'ok' | 'unstable'; since: string | null }>
  /** `manual`: o WhatsApp é assistido, a clínica manda à mão (P-ASS-21); `provider` na Autentique. */
  whatsappMode: 'provider' | 'manual'
  /** O provedor da clínica ativa (RN-ASS-33): decide o que o seletor de canal e o painel dizem. */
  signatureProvider: SignatureProvider
}

export interface SignatureRequest {
  id: string
  appointmentId: string
  protocolNumber: string
  channels: Channels
  recipient: {
    role: 'patient' | 'guardian'
    name: string
    docType: string | null
    docNumber: string | null
    whatsapp: string | null
    email: string | null
  }
  message: string | null
  sentAt: string
  linkIssuedAt: string
  linkExpiresAt: string
  linkOpenedAt: string | null
  resendCount: number
  delivery: DeliveryItem[]
  resends: { at: string; by: string | null; channels: Channels }[]
  clinicSigner: {
    mode: 'manual' | 'automatic'
    userId: string | null
    name: string | null
    role: 'manager' | 'practitioner' | 'root' | null
    signedAt: string | null
  }
  anamnesis: { mode: string; anamnesisId: string | null } | null
  canceledAt: string | null
  canceledBy: { id: string | null; name: string } | null
  cancellationReason: string | null
  undoneAt: string | null
  pendingAction: PendingAction | null
  createdBy: { id: string | null; name: string } | null
  /** Quem conduz este envio; o envio vivo continua no provedor em que nasceu (RN-ASS-33). */
  provider: SignatureProvider
  /** O link da Autentique da paciente; nulo até o documento existir lá (RN-ASS-36). */
  externalLink: string | null
  /** O link vivo, para "Copiar link" e o WhatsApp assistido; na Autentique, o `externalLink`. */
  link: string | null
  whatsappText: string | null
  whatsappMode: 'provider' | 'manual'
}

export interface ProofSignature {
  id: string
  role: 'patient' | 'guardian' | 'clinic_signer'
  mode: 'westhetics' | 'manual' | 'automatic' | 'external_provider'
  signerRole: 'manager' | 'practitioner' | 'root' | null
  signer: {
    name: string
    docType: string | null
    docNumber: string | null
    foreignDocument: { type: string; number: string; country: string } | null
    userId: string | null
    email: string | null
  }
  signedAt: string
  isSignedAtEstimated: boolean
  ip: string | null
  device: string | null
  deviceDetails: Record<string, unknown> | null
  copySentTo: string | null
  documents: { documentId: string; hash: string; readAt?: string | null; scale?: string }[]
  acceptance: { evidenceNotice?: string; acceptanceText?: string; at?: string } | null
  renderVersion: string | null
  hasDrawnSignature: boolean
  drawnSignature: { format: 'png'; fileUrl: string } | null
  hash: string | null
  /** Só na assinatura feita no provedor (`mode: external_provider`). */
  externalProvider?: {
    provider: string
    externalReference: string | null
    signerPublicId?: string | null
  } | null
}

export interface TimelineItem {
  at: string
  type: string
  channel: 'whatsapp' | 'email' | null
  /** `integration` desde a T53: o envio pedido por um app de integração, pela API ou pelo MCP. */
  source: 'user' | 'system' | 'provider' | 'patient' | 'integration'
  userName: string | null
  /** O nome do app quando `source` é `integration`. */
  integrationName: string | null
  detail: string | null
}

export interface SignatureDetail extends DerivedStatus {
  appointmentId: string
  type: 'procedure' | 'standalone'
  patient: {
    id: string
    name: string
    isMinor: boolean
    whatsapp: string | null
    email: string | null
    isArchived: boolean
  }
  procedure: string | null
  practitioner: { id: string; name: string } | null
  totalAmountCents: number | null
  procedureDate: string | null
  generatedAt: string
  clinic: {
    id: string
    name: string
    signatureMode: 'manual' | 'automatic'
    signatureProvider: SignatureProvider
    /** A clínica tem o token da Autentique da conta do e-mail jurídico: assina daqui (RN-ASS-37). */
    canSignInProvider: boolean
    /** O e-mail jurídico, para a frase de caminho da assinatura na Autentique. */
    legalEmail?: string | null
  }
  signatureRequest: SignatureRequest | null
  documents: SignatureDocument[]
  signatures: ProofSignature[]
  timeline: TimelineItem[]
  actions: RowAction[]
  deliverySummary: string
}

export interface SendResult {
  signatureRequest: SignatureRequest
  actionId: string
  undoableUntil: string
  documents: SignatureDocument[]
  patientFirstName: string
  patientName: string
}

export interface ResendResult {
  signatureRequest: SignatureRequest
  status: 'awaiting_patient'
  actionId: string
  undoableUntil: string
  patientFirstName: string
  patientName: string
}

export interface BatchResendResult {
  actionId: string
  undoableUntil: string
  resent: { appointmentId: string; patient: string }[]
  skipped: { appointmentId: string; patient: string | null; reason: string }[]
}

export interface CancelResult {
  status: 'canceled'
  documents: { id: string; status: string }[]
  actionId: string
  undoableUntil: string
  patientFirstName: string
  patientName: string
}

export interface SignAsClinicResult {
  signature: ProofSignature
  status: 'signed'
  signedAt: string
  documents: { id: string; status: string; download: string }[]
}

export interface PushStatus {
  vapidPublicKey: string | null
  isAvailable: boolean
  isSubscribed: boolean
  subscriptionCount: number
}

export interface SignatureListParams {
  filter: SignatureFilter
  from?: string
  to?: string
  search?: string
  patientId?: string
  sort: SignatureSort
  page: number
  perPage?: number
}

export const signatureKeys = {
  all: ['signatures'] as const,
  lists: ['signatures', 'list'] as const,
  list: (params: SignatureListParams) => ['signatures', 'list', params] as const,
  detail: (appointmentId: string) => ['signatures', 'detail', appointmentId] as const,
  push: ['signatures', 'push'] as const,
}

/** O que muda quando um envio muda: a lista, o painel, a aba Documentos da ficha e o dashboard. */
export function invalidateSignatures(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: signatureKeys.all }),
    queryClient.invalidateQueries({ queryKey: ['documents'] }),
    queryClient.invalidateQueries({ queryKey: ['patients'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])
}

export function useSignatures(params: SignatureListParams) {
  const api = useApi()
  return useQuery({
    queryKey: signatureKeys.list(params),
    queryFn: () => {
      const query: Query = {
        filter: params.filter,
        from: params.from,
        to: params.to,
        search: params.search,
        patientId: params.patientId,
        sort: params.sort,
        page: params.page,
        perPage: params.perPage,
      }
      return api.list<SignatureRow, SignatureListMeta>('/signatures', query)
    },
    placeholderData: (previous) => previous,
  })
}

export function useSignature(appointmentId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: signatureKeys.detail(appointmentId ?? ''),
    queryFn: () => api.get<SignatureDetail>(`/signatures/${appointmentId}`),
    enabled: appointmentId !== null,
  })
}

export function useSendSignatureRequest() {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: { appointmentId: string; channels: Channels }) =>
      api.post<SendResult>('/signatures/requests', input, {
        idempotencyKey: idempotency.keyFor(input),
      }),
    onSuccess: () => {
      idempotency.reset()
      return invalidateSignatures(queryClient)
    },
  })
}

export function useResendSignature() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { appointmentId: string; channels: Channels }) =>
      api.post<ResendResult>(`/signatures/${input.appointmentId}/resend`, {
        channels: input.channels,
      }),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useBatchResend() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { appointmentIds: string[]; channels: Channels }) =>
      api.post<BatchResendResult>('/signatures/batch/resend', input),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useCancelSignature() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { appointmentId: string; reason?: string }) =>
      api.post<CancelResult>(
        `/signatures/${input.appointmentId}/cancel`,
        input.reason ? { reason: input.reason } : {},
      ),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useUndoSignatureAction() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (actionId: string) => api.post<void>(`/signatures/undo/${actionId}`, {}),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useSignAsClinic() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (appointmentId: string) =>
      api.post<SignAsClinicResult>(`/signatures/${appointmentId}/sign-as-clinic`, {}),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useDeleteSignedDocument() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { appointmentId: string; documentId: string; justification: string }) =>
      api.delete(`/signatures/${input.appointmentId}/documents/${input.documentId}`, {
        justification: input.justification,
      }),
    onSuccess: () => invalidateSignatures(queryClient),
  })
}

export function useSignedPdf() {
  const api = useApi()
  return useMutation({
    mutationFn: (input: { appointmentId: string; documentId: string }) =>
      api.download(`/signatures/${input.appointmentId}/documents/${input.documentId}/signed.pdf`),
  })
}

export function useSignaturesZip() {
  const api = useApi()
  return useMutation({
    mutationFn: (appointmentIds: string[]) =>
      api.download('/signatures/batch/zip', { appointmentIds: appointmentIds.join(',') }),
  })
}

export function usePushStatus(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: signatureKeys.push,
    queryFn: () => api.get<PushStatus>('/signatures/notifications'),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRegisterPush() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      endpoint: string
      keys: { p256dh: string; auth: string }
      userAgent?: string
    }) => api.put<void>('/signatures/notifications/subscription', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: signatureKeys.push }),
  })
}

export function useUnregisterPush() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (endpoint: string) =>
      api.delete('/signatures/notifications/subscription', { endpoint }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: signatureKeys.push }),
  })
}

/** Entrega o arquivo baixado ao navegador com o nome que a API deu (RN-DOC-28). */
export function saveDownloadedFile(file: DownloadedFile, fallbackName: string) {
  const url = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.fileName ?? fallbackName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
